import { Link } from "react-router-dom";
import { useEffect } from "react";

const LAST_UPDATED = "June 2025";
const COMPANY = "OneCheckUp";
const WEBSITE = "https://1checkup.com";
const CONTACT_EMAIL = "onecheckup@gmail.com";

export default function PrivacyPage() {
  useEffect(() => { document.title = `Privacy Policy | ${COMPANY}`; }, []);

  return (
    <div style={{ minHeight: "100vh", background: "hsl(var(--background))", fontFamily: "inherit" }}>
      {/* Header */}
      <header style={{ borderBottom: "1px solid hsl(var(--border))", padding: "1rem 2rem", display: "flex", alignItems: "center", justifyContent: "space-between", background: "hsl(var(--card))" }}>
        <span style={{ fontWeight: 700, fontSize: "1rem", color: "hsl(var(--foreground))" }}>{COMPANY}</span>
        <Link to="/login" style={{ fontSize: "0.8125rem", color: "hsl(var(--primary))", textDecoration: "none" }}>← Back to login</Link>
      </header>

      <main style={{ maxWidth: "780px", margin: "0 auto", padding: "3rem 2rem 5rem" }}>
        {/* Title */}
        <div style={{ marginBottom: "2.5rem", paddingBottom: "1.5rem", borderBottom: "1px solid hsl(var(--border))" }}>
          <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", marginBottom: "0.5rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>Legal</p>
          <h1 style={{ fontSize: "1.875rem", fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: "0.5rem" }}>Privacy Policy</h1>
          <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>Last updated: {LAST_UPDATED} · Applies to all {COMPANY} users and their patients</p>
        </div>

        <div style={{ fontSize: "0.9rem", lineHeight: "1.8", color: "hsl(var(--foreground))" }}>

          {/* Intro callout */}
          <p style={{ marginBottom: "2rem", padding: "1rem 1.25rem", background: "hsl(var(--muted))", borderRadius: "8px", fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>
            {COMPANY} is a clinic management platform. This policy explains how we collect, use, store, and protect data — including patient health data — in compliance with Indian law, including the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 and the Digital Personal Data Protection Act, 2023 (DPDPA).
          </p>

          <Section title="1. Who We Are">
            <p>{COMPANY} is a software-as-a-service (SaaS) product designed for healthcare clinics in India. We provide tools for managing patients, appointments, prescriptions, and billing.</p>
            <p>In data protection terms, <strong>clinics using our platform are the Data Fiduciaries</strong> (controllers) for their patients' data. <strong>{COMPANY} acts as a Data Processor</strong> — we process patient data only on the clinic's instructions, for the purpose of providing the Service.</p>
          </Section>

          <Section title="2. Data We Collect">
            <p><strong>2.1 Clinic and Staff Account Data</strong></p>
            <p>When a clinic registers, we collect:</p>
            <ul>
              <li>Clinic name, address, phone number, email, and GSTIN</li>
              <li>Administrator and staff names, email addresses, and roles</li>
              <li>Subscription and billing information (we do not store full card numbers)</li>
            </ul>

            <p><strong>2.2 Patient Data (entered by the clinic)</strong></p>
            <p>Clinics enter patient data into the platform on behalf of their patients. This may include:</p>
            <ul>
              <li>Name, age, gender, contact number, address</li>
              <li>Medical history, complaints, diagnoses, and vital signs</li>
              <li>Prescriptions, medications, and dosage information</li>
              <li>Investigation and lab reports</li>
              <li>Billing and payment records</li>
              <li>IPD admission records</li>
            </ul>
            <p>Health and medical information is classified as <strong>Sensitive Personal Data or Information (SPDI)</strong> under the IT (SPDI) Rules, 2011, and as <strong>personal data</strong> under the DPDPA, 2023. It is handled with the highest level of protection.</p>

            <p><strong>2.3 Usage and Technical Data</strong></p>
            <ul>
              <li>IP addresses, browser type, and device information</li>
              <li>Pages visited, features used, and session duration (for service improvement)</li>
              <li>Error logs and performance data</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Data">
            <p>We use data only for the following purposes:</p>
            <ul>
              <li><strong>Providing the Service:</strong> Storing and retrieving clinic and patient records as directed by the clinic</li>
              <li><strong>Account management:</strong> Managing subscriptions, authentication, and support</li>
              <li><strong>Service improvement:</strong> Analysing usage patterns (in aggregate, without identifying individuals) to improve the platform</li>
              <li><strong>Security:</strong> Detecting and preventing fraud, abuse, and unauthorised access</li>
              <li><strong>Legal compliance:</strong> Meeting obligations under Indian law</li>
              <li><strong>Communications:</strong> Sending service updates, feature announcements, and billing notifications to clinic administrators</li>
            </ul>
            <p>We do <strong>not</strong> use patient health data for advertising, sell data to third parties, or use it for any purpose beyond operating the Service.</p>
          </Section>

          <Section title="4. Legal Basis for Processing">
            <p>Under the DPDPA, 2023, we process data on the following bases:</p>
            <ul>
              <li><strong>Contractual necessity:</strong> Processing required to provide the Service under our agreement with the clinic</li>
              <li><strong>Legitimate interests:</strong> Security monitoring, fraud prevention, and service improvement</li>
              <li><strong>Legal obligation:</strong> Compliance with applicable Indian laws</li>
            </ul>
            <p>For patient data specifically, the clinic is responsible for establishing the legal basis (typically patient consent) for collecting and processing patient health information. {COMPANY} processes that data as instructed by the clinic.</p>
          </Section>

          <Section title="5. Data Sharing and Disclosure">
            <p>We do not sell, rent, or trade your data. We may share data only in the following limited circumstances:</p>
            <ul>
              <li><strong>Service providers:</strong> Trusted third-party vendors who help us operate the platform (cloud hosting, email delivery, payment processing), bound by data processing agreements</li>
              <li><strong>Legal requirements:</strong> When required by court order, law enforcement, or applicable Indian law</li>
              <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, with prior notice to affected clinics</li>
              <li><strong>With your consent:</strong> Any other sharing will be only with your explicit written consent</li>
            </ul>

            <p><strong>Key sub-processors we use:</strong></p>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem", marginTop: "0.5rem" }}>
                <thead>
                  <tr style={{ background: "hsl(var(--muted))" }}>
                    {["Provider", "Purpose", "Location"].map(h => (
                      <th key={h} style={{ padding: "0.625rem 0.875rem", textAlign: "left", fontWeight: 600, borderBottom: "1px solid hsl(var(--border))" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Cloud hosting provider", "Data storage and compute", "India / AWS Mumbai"],
                    ["Email service provider", "Transactional emails", "India / Global"],
                    ["Payment gateway", "Subscription billing", "India"],
                  ].map(([provider, purpose, location], i) => (
                    <tr key={i} style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                      <td style={{ padding: "0.625rem 0.875rem" }}>{provider}</td>
                      <td style={{ padding: "0.625rem 0.875rem", color: "hsl(var(--muted-foreground))" }}>{purpose}</td>
                      <td style={{ padding: "0.625rem 0.875rem", color: "hsl(var(--muted-foreground))" }}>{location}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="6. Data Storage and Security">
            <p>All data is stored on servers located in India. We implement the following security measures in compliance with the IT (Reasonable Security Practices) Rules, 2011:</p>
            <ul>
              <li>Encryption of data in transit (TLS 1.2+) and at rest (AES-256)</li>
              <li>Role-based access controls — staff can only access data relevant to their role</li>
              <li>Multi-tenant data isolation — each clinic's data is logically separated</li>
              <li>Automatic session timeouts and secure authentication</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Audit logs of data access and modifications</li>
            </ul>
            <p>In the event of a data breach that is likely to result in harm, we will notify affected clinics and, where required, the relevant authorities, within 72 hours of becoming aware of the breach.</p>
          </Section>

          <Section title="7. Data Retention">
            <p>We retain data for the following periods:</p>
            <ul>
              <li><strong>Active clinic accounts:</strong> For the duration of the subscription plus 90 days after termination (to allow data export)</li>
              <li><strong>Patient records:</strong> As directed by the clinic, within the limits of applicable Indian medical record retention requirements (typically 3–7 years depending on the type of record)</li>
              <li><strong>Billing records:</strong> 8 years, as required under Indian accounting and tax law</li>
              <li><strong>Usage logs:</strong> 90 days</li>
              <li><strong>Security logs:</strong> 180 days</li>
            </ul>
            <p>After the retention period, data is securely deleted or anonymised.</p>
          </Section>

          <Section title="8. Your Rights">
            <p>Under the DPDPA, 2023 and the IT (SPDI) Rules, 2011, clinic administrators have the following rights regarding their own account data:</p>
            <ul>
              <li><strong>Right of access:</strong> Request a copy of data we hold about your clinic account</li>
              <li><strong>Right of correction:</strong> Request correction of inaccurate account data</li>
              <li><strong>Right of erasure:</strong> Request deletion of your account data (subject to legal retention obligations)</li>
              <li><strong>Right to data portability:</strong> Request an export of your clinic's data in a machine-readable format</li>
              <li><strong>Right to withdraw consent:</strong> Where processing is based on consent, withdraw it at any time</li>
              <li><strong>Right to grievance redressal:</strong> Lodge a complaint with our Grievance Officer</li>
            </ul>
            <p>For patient rights (access, correction, erasure of their health records), patients must contact their clinic directly. The clinic, as Data Fiduciary, is responsible for responding to patient rights requests.</p>
            <p>To exercise your rights, contact: <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "hsl(var(--primary))" }}>{CONTACT_EMAIL}</a>. We will respond within 30 days.</p>
          </Section>

          <Section title="9. Cookies">
            <p>We use the following types of cookies:</p>
            <ul>
              <li><strong>Strictly necessary:</strong> Session authentication tokens required for the platform to function. Cannot be disabled.</li>
              <li><strong>Functional:</strong> Remembering user preferences such as theme and language settings.</li>
              <li><strong>Analytics:</strong> Aggregated, anonymised usage data to improve the platform. No individual patient data is used in analytics.</li>
            </ul>
            <p>We do not use advertising cookies or share cookie data with advertising networks.</p>
          </Section>

          <Section title="10. Children's Privacy">
            <p>The platform is intended for use by healthcare professionals and clinic administrators. We do not knowingly collect personal data from individuals under 18 for account registration purposes.</p>
            <p>Patient records may include data about minors, entered by the treating clinic as part of clinical care. Such data is handled with the same protections as all other patient data.</p>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>We may update this Privacy Policy to reflect changes in our practices or applicable law. When we make material changes, we will:</p>
            <ul>
              <li>Update the "Last updated" date at the top of this page</li>
              <li>Notify clinic administrators via email and/or in-app notification at least 14 days before the changes take effect</li>
            </ul>
            <p>Continued use of the Service after the effective date constitutes acceptance of the updated policy.</p>
          </Section>

          <Section title="12. Grievance Officer">
            <p>In accordance with the Information Technology Act, 2000, the IT (SPDI) Rules, 2011, and the DPDPA, 2023, the following person is designated as the Grievance Officer for data-related complaints:</p>
            <div style={{ background: "hsl(var(--muted))", borderRadius: "8px", padding: "1rem 1.25rem", marginTop: "0.75rem", fontSize: "0.875rem" }}>
              <p style={{ margin: "0 0 0.25rem" }}><strong>Name:</strong> Komal Dhandar</p>
              <p style={{ margin: "0 0 0.25rem" }}><strong>Designation:</strong> Chief Executive Officer, {COMPANY}</p>
              <p style={{ margin: "0 0 0.25rem" }}><strong>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "hsl(var(--primary))" }}>{CONTACT_EMAIL}</a></p>
              <p style={{ margin: "0 0 0.25rem" }}><strong>Address:</strong> [Street Address], Pune, Maharashtra - 411051</p>
              <p style={{ margin: 0 }}><strong>Response time:</strong> Complaints acknowledged within 48 hours; resolved within 30 days</p>
            </div>
            <p style={{ marginTop: "0.75rem", fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>
              If you are not satisfied with our response, you may approach the Data Protection Board of India once it is established under the DPDPA, 2023.
            </p>
          </Section>

          <Section title="13. Contact Us">
            <ul>
              <li>Email: <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "hsl(var(--primary))" }}>{CONTACT_EMAIL}</a></li>
              <li>Website: <a href={WEBSITE} target="_blank" rel="noopener noreferrer" style={{ color: "hsl(var(--primary))" }}>{WEBSITE}</a></li>
              <li>Terms of Service: <Link to="/terms" style={{ color: "hsl(var(--primary))" }}>View Terms</Link></li>
            </ul>
          </Section>

        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: "2.25rem" }}>
      <h2 style={{ fontSize: "1rem", fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: "0.875rem", paddingBottom: "0.5rem", borderBottom: "1px solid hsl(var(--border))" }}>
        {title}
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        {children}
      </div>
    </section>
  );
}
